import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Pagination,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { Upload as UploadIcon, Download as DownloadIcon } from "@mui/icons-material";
import Toolbar from "../../../../components/core/base/Toolbar";
import DataTable from "../../../../components/core/base/DataTable";
import api from "../../../../components/api/api";
import assetsConfig from "../../config/assetsConfig"; // Конфигурация для отображения активов
import assetActions from "./AssetActions";
import AssetStatusSummary from './AssetStatusSummary';
import ColumnsModal from "../../../../components/core/base/ColumnsModal"; // Модальное окно для колонок
import FiltersModal from "../../../../components/core/base/FiltersModal"; // Модальное окно для фильтров
import filtersConfig from "../../../core/config/filtersConfig";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack"; // Для уведомлений

// Ключ для сохранения состояния таблицы в localStorage
const TABLE_STATE_KEY = "table_state_rental_assets";

// Функция для загрузки сохраненного состояния таблицы
const loadTableState = () => {
  try {
    const saved = localStorage.getItem(TABLE_STATE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch (error) {
    console.warn("Ошибка загрузки состояния таблицы:", error);
  }
  return null;
};

// Функция для сохранения состояния таблицы
const saveTableState = (state) => {
  try {
    localStorage.setItem(TABLE_STATE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("Ошибка сохранения состояния таблицы:", error);
  }
};

const AssetsPage = () => {
  // Загружаем сохраненное состояние
  const [savedState] = useState(loadTableState);
  const requestVersion = useRef(0);
  const skipSearchEffect = useRef(true);
  const searchTimer = useRef(null);
  const [loadError, setLoadError] = useState('');
  
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]); // Конфигурация колонок
  const [viewMode, setViewMode] = useState("list"); // Режим отображения
  const [isModalOpen, setModalOpen] = useState(false); // Управление модальным окном
  const [isColumnsModalOpen, setIsColumnsModalOpen] = useState(false); // Управление модальным окном для колонок
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false); // Управление модальным окном для фильтров
  const [selectedItems, setSelectedItems] = useState([]); // Выбранные элементы
  const [filters, setFilters] = useState([]); // Состояние для фильтров
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exportDialog, setExportDialog] = useState(false);
  const [exportMode, setExportMode] = useState("all"); // "all" или "selected"
  const [exporting, setExporting] = useState(false);
  // const [businessTypeKey, setBusinessTypeKey] = useState(null); // Убрано - больше не нужно
  const [searchQuery, setSearchQuery] = useState(savedState?.searchQuery || "");
  // Состояние для активных фильтров
  const [activeFilters, setActiveFilters] = useState(savedState?.activeFilters || {});
  
  // Пагинация (с восстановлением из localStorage)
  const [page, setPage] = useState(savedState?.page || 1);
  const [pageSize, setPageSize] = useState(savedState?.pageSize || 20);
  const [totalAssets, setTotalAssets] = useState(0);
  
  // Сортировка (серверная, с восстановлением из localStorage)
  const [sortField, setSortField] = useState(savedState?.sortField || null);
  const [sortOrder, setSortOrder] = useState(savedState?.sortOrder || "desc");
  
  // ID элемента для подсветки и скролла после возврата
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate(); // Инициализация navigate
  const tableKey = "rental_assets"; // Ключ таблицы
  const filtersConfigForAssets = filtersConfig["assets"]; // Конфигурация фильтров для активов

  // Сохраняем состояние таблицы при изменении
  useEffect(() => {
    saveTableState({ page, pageSize, sortField, sortOrder, searchQuery, activeFilters });
  }, [page, pageSize, sortField, sortOrder, searchQuery, activeFilters]);

  // Скролл к последнему просмотренному элементу после загрузки данных
  useEffect(() => {
    const lastViewedId = localStorage.getItem("lastViewedAssetId");
    if (lastViewedId && assets.length > 0 && !loading) {
      const id = parseInt(lastViewedId, 10);
      // Проверяем, есть ли элемент в текущем списке
      const elementExists = assets.some(asset => asset.id === id);
      if (elementExists) {
        setHighlightedRowId(id);
        // Скроллим к элементу с небольшой задержкой для рендера
        setTimeout(() => {
          const element = document.getElementById(`rental_assets-row-${id}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
        // Убираем подсветку через 2 секунды
        setTimeout(() => {
          setHighlightedRowId(null);
        }, 2000);
      }
      // Очищаем сохраненный ID
      localStorage.removeItem("lastViewedAssetId");
    }
  }, [assets, loading]);

  // Загрузка активов
  const fetchAssets = async (query = "", filters = {}, currentPage = 1, currentPageSize = 50, currentSortField = null, currentSortOrder = "desc") => {
    const requestId = ++requestVersion.current;
    setLoadError('');
    setLoading(true);
    try {
      // Формируем параметры запроса
      const params = {
        page: currentPage,
        limit: currentPageSize
      };
      
      // Добавляем параметры сортировки
      if (currentSortField) {
        params.sort_by = currentSortField;
        params.sort_order = currentSortOrder;
      }

      // Добавляем поисковый запрос
      if (query) {
        params.search = query;
      }

      // 🔧 ИСПРАВЛЕНИЕ: Правильная обработка фильтра по статусу
      // Добавляем фильтры
      for (const [key, value] of Object.entries(filters)) {
        if (value != null && String(value).trim() !== "") {
          if (key === "status") {
            // Для статуса нужно конвертировать название в ID
            try {
              const statusesResponse = await api.get("/asset-statuses/", {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              });
              const statusObj = (statusesResponse.data?.items || statusesResponse.data).find(s => s.name === value);
              if (statusObj) {
                params.asset_status_id = statusObj.id;
              } else {
                params.status = value;
              }
            } catch (error) {
              console.error("Ошибка загрузки статусов для фильтрации:", error);
              params.status = value;
            }
          } else {
            params[key] = value;
          }
        }
      }

      console.log("🔍 Параметры запроса для фильтрации активов:", params);

      const response = await api.get("/assets/rental", {
        params,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      if (requestId !== requestVersion.current) return;
      // Проверяем формат ответа (пагинированный или нет)
      let assetsData = [];
      if (response.data.items) {
        // Пагинированный ответ
        assetsData = response.data.items;
        setTotalAssets(response.data.total);
      } else {
        // Старый формат (массив)
        assetsData = response.data;
        setTotalAssets(response.data.length);
      }
      
      // Добавляем rental_point_name и обрабатываем статус для каждого актива
      const assetsWithProcessedData = assetsData.map(asset => {
        return {
          ...asset,
          rental_point_name: asset.rental_point?.name || 'Не назначен',
          // Обрабатываем статус - берем из asset_status.name если есть, иначе из status
          status: asset.asset_status?.name || asset.status || 'Неизвестно'
        };
      });
      
      setAssets(assetsWithProcessedData);
    } catch (error) {
      if (requestId === requestVersion.current) setLoadError("Не удалось загрузить активы");
    } finally {
      if (requestId === requestVersion.current) setLoading(false);
    }
  };

  // Обработчик сортировки (серверная)
  const handleSort = (field) => {
    let newSortOrder = "asc";
    if (sortField === field) {
      // Если кликнули на то же поле - меняем направление
      newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    }
    setSortField(field);
    setSortOrder(newSortOrder);
    // Загружаем данные с новой сортировкой
    fetchAssets(searchQuery, activeFilters, page, pageSize, field, newSortOrder);
  };

  // Удаление выбранных активов
  const handleDeleteSelected = async () => {
    console.log("🗑️ handleDeleteSelected вызван, selectedItems:", selectedItems);
    if (selectedItems.length === 0) {
      enqueueSnackbar("Выберите активы для удаления", { variant: "warning" });
      return;
    }
    try {
      for (let id of selectedItems) {
        console.log("🗑️ Удаляю актив ID:", id);
        await api.delete(`/assets/hard-remove/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
      }
      setSelectedItems([]);
      fetchAssets(searchQuery, activeFilters, page, pageSize, sortField, sortOrder);
      enqueueSnackbar(`Удалено ${selectedItems.length} активов`, { variant: "success" });
    } catch (error) {
      console.error("Ошибка удаления выбранных активов:", error);
      enqueueSnackbar("Ошибка удаления активов", { variant: "error" });
    }
  };

  // Удаление актива
  const handleDelete = async (id) => {
    try {
      const response = await api.delete(`/assets/rental/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (response.status === 200) {
        fetchAssets(searchQuery, activeFilters, page, pageSize, sortField, sortOrder);
        enqueueSnackbar(`Актив с ID ${id} успешно удалён!`, {
          variant: "success",
        });
      }
    } catch (error) {
      // Ошибка удаления актива
    }
  };

  // Загрузка колонок
  const fetchColumns = async () => {
    try {
      const response = await api.get("/table-columns/", {
        params: { table_key: "rental_assets" },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Преобразуем колонки из базы данных
      const dbColumns = response.data;

      // Фильтруем колонки - исключаем неинформативные технические поля
      const excludedColumns = ['is_deleted', 'deleted_at', 'specifications', 'search_metadata', 'ai_tags', 'ai_updated_at'];
      const filteredColumns = dbColumns.filter(col => !excludedColumns.includes(col.column_name));

      const transformedColumns = filteredColumns.map((col) => ({
        id: col.id,
        table_key: col.table_key,
        column_name: col.column_name,
        field: col.column_name,
        label: translateLabelToRussian(col.column_name),
        sortable: col.sortable,
        is_visible: col.is_visible,
      }));

      setColumns(transformedColumns);
    } catch (error) {
      // Ошибка загрузки колонок - используем статическую конфигурацию
      const fallbackColumns = assetsConfig.map((config, index) => ({
        id: index,
        table_key: "rental_assets",
        column_name: config.field,
        field: config.field,
        label: config.label,
        sortable: config.sortable,
        is_visible: config.is_visible !== undefined ? config.is_visible : true,
      }));
      setColumns(fallbackColumns);
    }
  };

  // Функция для перевода на русский язык
  const translateLabelToRussian = (columnName) => {
    const translations = {
      id: "Идентификатор",
      asset_number: "Номер актива",
      type: "Тип",
      name: "Название",
      description: "Описание",
      custom_fields: "Пользовательские поля",
      company_id: "ID компании",
      founder_id: "ID основателя",
      staff_id: "ID сотрудника",
      created_at: "Дата создания",
      updated_at: "Дата обновления",
      sku: "Артикул",
      inventory_number: "Инвентарный номер",
      status: "Статус",
      custom_statuses: "Пользовательские статусы",
      rental_point_id: "ID пункта проката",
      rental_point: "Пункт проката",
      rental_point_name: "Пункт проката",
      "rental_point.name": "Пункт проката",
      category: "Категория",
      deposit: "Депозит",
      purchase_price: "Цена покупки",
      purchase_date: "Дата покупки",
      maintenance_due_date: "Дата обслуживания",
      maintenance_date: "Дата обслуживания",
      images: "Изображения",
      value: "Стоимость",
      meta_data: "Дополнительные данные",
      total_revenue: "Общий доход",
      rental_count: "Количество аренд",
      last_rental_date: "Дата последней аренды",
      profitability_date: "Дата окупаемости",
      is_deleted: "Удален",
      deleted_at: "Дата удаления",
      specifications: "Спецификации",
      search_metadata: "Метаданные поиска",
      ai_tags: "AI теги",
      ai_updated_at: "Дата обновления AI",
    };
    return translations[columnName] || columnName;
  };

  // Загрузка фильтров
  const initializeFilters = async () => {
    try {
      // Загружаем статусы и пункты проката для фильтров
      const [statusesResponse, rentalPointsResponse] = await Promise.all([
        api.get("/asset-statuses/", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
        api.get("/rental-points/", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }),
      ]);

      // Извлекаем данные из ответов (могут быть пагинированными)
      const statusesData = statusesResponse.data?.items || statusesResponse.data || [];
      const rentalPointsData = rentalPointsResponse.data?.items || rentalPointsResponse.data || [];

      const statuses = statusesData.map((s) => s.name);
      const rentalPoints = rentalPointsData.map((rp) => rp.name);

      // Обновляем конфигурацию фильтров с динамическими данными
      const dynamicFiltersConfig = filtersConfigForAssets.map((filter) => {
        if (filter.field === "status") {
          return { ...filter, options: statuses };
        }
        if (filter.field === "rental_point.name") {
          return { ...filter, options: rentalPoints };
        }
        return filter;
      });

      // Преобразуем в формат для FiltersModal
      const filters = dynamicFiltersConfig.map((filter) => ({
        filter_name: filter.field,
        filter_label: filter.label,
        filter_type: filter.type,
        options: filter.options || [],
        is_visible: filter.isVisible,
        table_key: tableKey,
      }));

      setFilters(filters);
    } catch (error) {
      // Ошибка инициализации фильтров

      // Попытаемся загрузить данные по отдельности
      let statuses = [];
      let rentalPoints = [];

      try {
        const statusesResponse = await api.get("/asset-statuses/", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const statusesData = statusesResponse.data?.items || statusesResponse.data || [];
        statuses = statusesData.map((s) => s.name);
      } catch (e) {
        // Не удалось загрузить статусы
      }

      try {
        const rentalPointsResponse = await api.get("/rental-points/", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        const rentalPointsData = rentalPointsResponse.data?.items || rentalPointsResponse.data || [];
        rentalPoints = rentalPointsData.map((rp) => rp.name);
      } catch (e) {
        // Не удалось загрузить пункты проката
      }

      // Обновляем конфигурацию с тем, что удалось загрузить
      const fallbackFiltersConfig = filtersConfigForAssets.map((filter) => {
        if (filter.field === "status") {
          return { ...filter, options: statuses };
        }
        if (filter.field === "rental_point.name") {
          return { ...filter, options: rentalPoints };
        }
        return filter;
      });

      const fallbackFilters = fallbackFiltersConfig.map((filter) => ({
        filter_name: filter.field,
        filter_label: filter.label,
        filter_type: filter.type,
        options: filter.options || [],
        is_visible: filter.isVisible,
        table_key: tableKey,
      }));

      setFilters(fallbackFilters);
    }
  };
  // Загрузка бизнес-ключа - больше не нужна
  /*
  const fetchBusinessTypeKey = async () => {
    try {
      const response = await api.get("/settings/settings", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data?.business_type_key) {
        setBusinessTypeKey(response.data.business_type_key);
      }
    } catch (error) {
      console.error("Ошибка загрузки ключа бизнеса:", error);
    }
  };
  */

  // Копирование актива: переход на страницу создания с заполненными данными
  const handleCopy = (id) => {
    const asset = assets.find((a) => a.id === id);
    if (asset) {
      // Удаляем id и другие поля, которые не нужны при копировании
      const {
        id,
        created_at,
        updated_at,
        company_id,
        founder_id,
        manager_id,
        tariff_id, // Сбрасываем tariff_id при копировании
        tariff, // Сбрасываем название тарифа при копировании
        ...copyData
      } = asset;

      // Добавляем к имени " (копия)"
      copyData.name = copyData.name ? copyData.name + " (копия)" : "";

      // Передаём данные через state
      navigate("/assets/edit/new", { state: { copyData } });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    clearTimeout(searchTimer.current);
    setPage(1);
    fetchAssets(searchQuery, activeFilters, 1, pageSize, sortField, sortOrder);
  };

  // Очистка поиска
  const handleSearchClear = () => {
    skipSearchEffect.current = searchQuery !== "";
    clearTimeout(searchTimer.current);
    setSearchQuery("");
    setPage(1);
    fetchAssets("", activeFilters, 1, pageSize, sortField, sortOrder);
  };

  // Полное удаление актива
  const handlePermanentDelete = async (id) => {
    try {
      await api.delete(`/assets/rental/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      fetchAssets(searchQuery, activeFilters, page, pageSize, sortField, sortOrder);
      enqueueSnackbar(`Актив с ID ${id} удалён!`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Ошибка удаления актива", { variant: "error" });
    }
  };
  // Сохранение настроек колонок
  const handleColumnsChange = async (updatedColumns) => {
    try {
      // Преобразуем колонки в формат для bulk update
      const updates = updatedColumns.map((col) => ({
        id: col.id,
        is_visible: col.is_visible,
      }));

      // Отправляем обновления на сервер
      await api.put("/table-columns/bulk-update-visibility", updates, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Обновляем локальное состояние
      setColumns(updatedColumns);

      enqueueSnackbar("Настройки колонок сохранены", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar("Ошибка сохранения настроек колонок", {
        variant: "error",
      });
    }
  };

  // Обработка применения фильтров
  const handleApplyFilters = (filterValues) => {
    setActiveFilters(filterValues);
    setPage(1); // Сбрасываем на первую страницу
    // Загружаем активы с новыми фильтрами
    fetchAssets(searchQuery, filterValues, 1, pageSize, sortField, sortOrder);
  };

  // Сброс фильтров
  const handleResetFilters = () => {
    setActiveFilters({});
    setPage(1); // Сбрасываем на первую страницу
    fetchAssets(searchQuery, {}, 1, pageSize, sortField, sortOrder);
  };
  useEffect(() => {
    // fetchBusinessTypeKey(); // Убрано - больше не нужно
    loadSavedFiltersAndFetchAssets();
    fetchColumns();
    initializeFilters();
  }, []);

  // Функция для загрузки сохраненных фильтров и применения их к данным
  const loadSavedFiltersAndFetchAssets = async () => {
    try {
      // Загружаем сохраненные фильтры пользователя из filter_config
      const response = await api.get(`/filters/${tableKey}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      
      if (requestVersion.current > 0) return;
      if (response.data && response.data.length > 0) {
        // Восстанавливаем значения фильтров из сохраненных данных
        const savedFilters = {};
        const activeFiltersForAPI = {};
        response.data.forEach(filter => {
          if (filter.filter_type === "value" && filter.options && filter.options.length > 0) {
            const value = filter.options[0] ?? "";
            // Сохраняем все значения для отображения в FiltersModal
            savedFilters[filter.filter_name] = value;
            
            // Добавляем в activeFiltersForAPI только непустые значения (для активной фильтрации)
            if (String(value).trim() !== "") {
              activeFiltersForAPI[filter.filter_name] = value;
            }
          }
        });
        
        setActiveFilters(savedFilters); // Устанавливаем все фильтры для отображения
        // Загружаем данные только с непустыми фильтрами
        fetchAssets(searchQuery, activeFiltersForAPI, page, pageSize, sortField, sortOrder);
      } else {
        // Если сохраненных фильтров нет, загружаем данные без фильтров
        fetchAssets(searchQuery, activeFilters, page, pageSize, sortField, sortOrder);
      }
    } catch (error) {
      // Ошибка загрузки сохраненных фильтров - загружаем данные без фильтров
      fetchAssets(searchQuery, activeFilters, page, pageSize, sortField, sortOrder);
    }
  };

  // Автоматический поиск с задержкой при изменении searchQuery
  useEffect(() => {
    // Пропускаем первый вызов при инициализации
    if (skipSearchEffect.current) { skipSearchEffect.current = false; return; }
    
    searchTimer.current = setTimeout(() => {
      setPage(1); // Сбрасываем на первую страницу при поиске
      fetchAssets(searchQuery, activeFilters, 1, pageSize, sortField, sortOrder);
    }, 500); // Задержка 500мс

    return () => clearTimeout(searchTimer.current);
  }, [searchQuery]); // Триггер при изменении поискового запроса

  // Обработка импорта активов
  const handleImport = async () => {
    if (!importFile) {
      enqueueSnackbar("Выберите файл для импорта", { variant: "warning" });
      return;
    }

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await api.post("/assets/import", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      enqueueSnackbar(
        `Импортировано: ${response.data.imported_assets ?? response.data.imported ?? 0}, Сделок: ${response.data.imported_deals ?? 0}, Пропущено: ${response.data.skipped ?? 0}`,
        { variant: response.data.errors?.length ? "warning" : "success" }
      );
      
      for (const message of response.data.errors || []) enqueueSnackbar(message, { variant: "warning" });
      setImportDialog(false);
      setImportFile(null);
      setPage(1); // Сбрасываем на первую страницу
      fetchAssets(searchQuery, activeFilters, 1, pageSize, sortField, sortOrder); // Обновляем список
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || "Ошибка импорта активов", {
        variant: "error",
      });
    } finally {
      setImporting(false);
    }
  };

  // Скачивание шаблона для импорта
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/assets/import/template", {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template_assets.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      enqueueSnackbar("Шаблон загружен", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Ошибка загрузки шаблона", { variant: "error" });
    }
  };

  // Экспорт активов
  const handleExport = async () => {
    setExporting(true);
    try {
      // Определяем какие ID экспортировать
      const assetIds = exportMode === "selected" && selectedItems.length > 0 
        ? selectedItems 
        : null;

      const response = await api.post("/assets/export", 
        { asset_ids: assetIds },
        {
          responseType: "blob",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `assets_export_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      enqueueSnackbar(
        exportMode === "selected" 
          ? `Экспортировано ${selectedItems.length} активов` 
          : "Все активы экспортированы", 
        { variant: "success" }
      );
      setExportDialog(false);
    } catch (error) {
      enqueueSnackbar(error.response?.data?.detail || "Ошибка экспорта активов", {
        variant: "error",
      });
    } finally {
      setExporting(false);
    }
  };

  // Открытие диалога экспорта
  const handleOpenExportDialog = () => {
    // Если есть выбранные элементы - предлагаем выбор
    if (selectedItems.length > 0) {
      setExportMode("selected");
    } else {
      setExportMode("all");
    }
    setExportDialog(true);
  };

  return (
    <>
      <Toolbar
        currentPage="assets"
        // businessTypeKey={businessTypeKey} // Убрано - больше не нужно
        onAddClick={() => navigate("/assets/edit/new")}
        onViewChange={(view) => setViewMode(view)}
        onRefresh={() => fetchAssets(searchQuery, activeFilters, page, pageSize, sortField, sortOrder)}
        onExport={handleOpenExportDialog}
        onOpenColumnsModal={() => setIsColumnsModalOpen(true)}
        onOpenFiltersModal={() => setIsFiltersModalOpen(true)}
        totalRecords={totalAssets}
        recordsLabel="активов"
        pageSize={pageSize}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setPage(1);
          fetchAssets(searchQuery, activeFilters, 1, newSize, sortField, sortOrder);
        }}
        onDeleteSelected={handleDeleteSelected}
        selectedItems={selectedItems}
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        onSearchSubmit={handleSearchSubmit}
        onSearchClear={handleSearchClear}
        customButtons={[
          {
            label: "Импорт",
            icon: <UploadIcon />,
            onClick: () => setImportDialog(true),
          },
          {
            label: "Экспорт",
            icon: <DownloadIcon />,
            onClick: handleOpenExportDialog,
          },
        ]}
      />
      <AssetStatusSummary refreshKey={assets} />
      {loadError && <Alert severity="error">{loadError}</Alert>}
      <DataTable
        selectedItems={selectedItems}
        data={assets}
        loading={loading}
        config={
          columns.length > 0
            ? columns.filter((col) => col.is_visible)
            : assetsConfig
        }
        viewMode={viewMode}
        onToolbarData={({ selectedItems: items }) => {
          console.log("📋 DataTable передал selectedItems:", items);
          setSelectedItems(items);
        }}
        actions={assetActions}
        onCopy={handleCopy}
        navigate={navigate}
        onPermanentDelete={handlePermanentDelete}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={handleSort}
        highlightedRowId={highlightedRowId}
        tableKey="rental_assets"
      />

      {/* Панель пагинации */}
      {Math.ceil(totalAssets / pageSize) > 1 && (
        <Box sx={{ 
          mt: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center'
        }}>
          <Typography variant="body2" color="text.secondary">
            Показано {assets.length} из {totalAssets} активов
          </Typography>
          <Pagination
            count={Math.ceil(totalAssets / pageSize)}
            page={page}
            onChange={(event, value) => {
              setPage(value);
              fetchAssets(searchQuery, activeFilters, value, pageSize, sortField, sortOrder);
            }}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <ColumnsModal
        open={isColumnsModalOpen}
        onClose={() => setIsColumnsModalOpen(false)}
        config={columns}
        onColumnsChange={handleColumnsChange}
      />
      <FiltersModal
        open={isFiltersModalOpen}
        onClose={() => setIsFiltersModalOpen(false)}
        filters={filters || []}
        activeFilters={activeFilters}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
        tableKey={tableKey}
      />

      {/* Диалог импорта */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Импорт активов из файла</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleDownloadTemplate}
              sx={{ mb: 2 }}
            >
              Скачать шаблон Excel
            </Button>
          </Box>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Заполните шаблон и загрузите его для импорта активов.
          </Typography>
          <Typography variant="caption" component="div" sx={{ mb: 2, color: 'text.secondary' }}>
            <strong>Обязательные поля:</strong> Название<br/>
            <strong>Дополнительные:</strong> Описание, Артикул, Инвентарный номер, Категория, 
            Статус, Пункт проката, Депозит, Цена покупки, Дата покупки, Дата ТО
          </Typography>
          
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
            fullWidth
          >
            Выбрать файл
            <input
              type="file"
              hidden
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setImportFile(e.target.files[0])}
            />
          </Button>
          
          {importFile && (
            <Typography variant="body2" sx={{ mt: 2 }}>
              Выбран файл: {importFile.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>
            Отмена
          </Button>
          <Button 
            onClick={handleImport} 
            variant="contained" 
            disabled={!importFile || importing}
          >
            {importing ? "Импорт..." : "Импортировать"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог экспорта */}
      <Dialog open={exportDialog} onClose={() => setExportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Экспорт активов</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Выберите, какие активы экспортировать:
          </Typography>
          
          <RadioGroup
            value={exportMode}
            onChange={(e) => setExportMode(e.target.value)}
          >
            <FormControlLabel 
              value="all" 
              control={<Radio />} 
              label={`Все активы (${totalAssets})`}
            />
            <FormControlLabel 
              value="selected" 
              control={<Radio />} 
              label={`Выбранные активы (${selectedItems.length})`}
              disabled={selectedItems.length === 0}
            />
          </RadioGroup>

          {selectedItems.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Чтобы экспортировать выбранные, отметьте активы чекбоксами в таблице
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialog(false)} disabled={exporting}>
            Отмена
          </Button>
          <Button 
            onClick={handleExport} 
            variant="contained" 
            disabled={exporting || (exportMode === "selected" && selectedItems.length === 0)}
            startIcon={<DownloadIcon />}
          >
            {exporting ? "Экспорт..." : "Экспортировать"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssetsPage;
